import { TranslatedLabel } from '@gm-commercial/translations';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Typography } from '@mui/material';
import MUIAutocomplete, {
  type AutocompleteChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TextField } from '../TextField';
import type {
  AutocompleteOption,
  AutocompleteProps,
} from './Autocomplete.model';
import useStyles, { generateAutocompleteCss } from './Autocomplete.styles';

/* Component function to generate a MUI Autocomplete with custom styling. */
const StyledAutocomplete = styled(MUIAutocomplete).withConfig({
  shouldForwardProp: (prop) =>
    !['error', 'errorLabel', 'helperTextLabel', 'theme'].includes(prop),
})<{ theme: Theme }>`
  ${({ theme }) => generateAutocompleteCss(theme)}
`;

const Autocomplete = <OptionType,>({
  name,
  options,
  value,
  onChange,
  label,
  error = false,
  errorLabel,
  helperTextLabel,
  placeholder,
  disabled = false,
  displayIcon = true,
  inputValue: controlledInputValue,
  onInputChange: controlledOnInputChange,
  required = false,
  displayAllOptionsByDefault = true,
  renderOption: customRenderOption,
  ...props
}: AutocompleteProps<OptionType>): JSX.Element => {
  const theme = useTheme<Theme>();
  const { classes } = useStyles();

  // Find the selected option based on the value
  const selectedOption = value
    ? (options.find((option) => option.value === value) ?? null)
    : null;

  const [internalInputValue, setInternalInputValue] = useState(
    selectedOption ? selectedOption.label : ''
  );

  // Use controlled inputValue if provided, otherwise use internal state
  const inputValue = controlledInputValue ?? internalInputValue;

  // Create filter function that uses MUI's default filtering
  const filterOptions = useMemo(
    () =>
      createFilterOptions<AutocompleteOption<OptionType>>({
        stringify: (option) => option.label,
      }),
    []
  );

  // Sync internal inputValue with value prop when it changes externally (only if not controlled)
  useEffect(() => {
    if (controlledInputValue === undefined) {
      if (selectedOption) {
        setInternalInputValue(selectedOption.label);
      } else {
        setInternalInputValue('');
      }
    }
  }, [selectedOption, controlledInputValue]);

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent,
      newOption: AutocompleteOption<OptionType> | null,
      reason: AutocompleteChangeReason
    ) => {
      if (reason === 'clear') {
        if (controlledInputValue === undefined) {
          setInternalInputValue('');
        }
        onChange(null);
        return;
      }
      if (newOption) {
        onChange(newOption.value);
        if (controlledInputValue === undefined) {
          setInternalInputValue(newOption.label);
        }
      } else {
        onChange(null);
      }
    },
    [onChange, controlledInputValue]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (controlledOnInputChange) {
        controlledOnInputChange(event, newInputValue, reason);
        return;
      }

      if (
        event &&
        (event.type === 'change' ||
          event.type === 'click' ||
          event.type === 'keydown')
      ) {
        if (controlledInputValue === undefined) {
          setInternalInputValue(newInputValue);
        }
        if (!newInputValue) {
          onChange(null);
        }
      }
    },
    [onChange, controlledInputValue, controlledOnInputChange]
  );

  /* Helper function to clean up style class name generation */
  const generateAutocompleteClassName = (): string => {
    const errorClass = error ? 'error' : '';
    const disabledClass = disabled ? 'disabled' : '';
    const noFocusClass = 'noFocus'; // Prevent focus styles when clicking dropdown

    return [props.className ?? '', errorClass, disabledClass, noFocusClass]
      .join(' ')
      .trim();
  };

  return (
    <StyledAutocomplete
      {...(props as Record<string, unknown>)}
      className={generateAutocompleteClassName()}
      popupIcon={displayIcon ? <KeyboardArrowDownIcon /> : null}
      id={name}
      data-testid={`${name}-autocomplete`}
      selectOnFocus={false}
      fullWidth
      options={options}
      getOptionLabel={(option) =>
        (option as AutocompleteOption<OptionType>).label
      }
      renderOption={
        customRenderOption
          ? (optionProps, option) =>
              customRenderOption(
                optionProps,
                option as AutocompleteOption<OptionType>
              )
          : (optionProps, option) => (
              <Box
                {...optionProps}
                component={'li'}
                key={optionProps.key}
                className={classes.option}
                data-testid={
                  (option as AutocompleteOption<OptionType>).dataTestId
                }
              >
                <Typography variant="small_body_regular">
                  {(option as AutocompleteOption<OptionType>).label}
                </Typography>
              </Box>
            )
      }
      autoComplete={false}
      includeInputInList
      inputValue={
        controlledInputValue !== undefined ? controlledInputValue : inputValue
      }
      onInputChange={controlledOnInputChange || handleInputChange}
      value={selectedOption}
      isOptionEqualToValue={(option, val) =>
        (option as AutocompleteOption<OptionType>).value ===
        (val as AutocompleteOption<OptionType>).value
      }
      filterOptions={(options, state) => {
        const allOptions = options as AutocompleteOption<OptionType>[];
        // If displayAllOptionsByDefault is true and there's no input value, show all options
        if (displayAllOptionsByDefault && !state.inputValue) {
          return allOptions;
        }
        // If there's input, use MUI's default filtering
        if (state.inputValue) {
          return filterOptions(allOptions, state);
        }
        // If no input and displayAllOptionsByDefault is false, return empty array to show no options message
        return [];
      }}
      openOnFocus={displayAllOptionsByDefault}
      noOptionsText={
        <TranslatedLabel
          translationKey="common:noResults"
          typographyVariant="small_body_regular"
        />
      }
      renderInput={(params) => (
        <TextField
          name={name}
          value={(params.inputProps as { value?: string })?.value ?? ''}
          error={error}
          errorLabel={errorLabel}
          helperTextLabel={helperTextLabel}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'off',
          }}
          InputProps={params.InputProps}
        />
      )}
      onChange={
        handleChange as (
          event: React.SyntheticEvent,
          value: unknown,
          reason: AutocompleteChangeReason
        ) => void
      }
      disabled={disabled}
      theme={theme}
    />
  );
};

export default Autocomplete;
